import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { ADVANCE, NOTICE, PHOTOS, TIERS } from '@/data/content'
import { measureDownloadMbps } from '@/libs/speedtest'
import type { AddonKey, AdvanceKey, AudienceMode, ClassKey, TierKey } from '@/types/Landing'

const NOTICE_LS_KEY = 'ob_notice_dismissed'

export const useLandingStore = defineStore('landing', () => {
    const mode = ref<AudienceMode>('retail')
    const tier = ref<TierKey>('base')
    const homeAddons = ref<Record<AddonKey, boolean>>({ mesh: false, power: false, staticip: false })
    const advance = ref<AdvanceKey>('monthly')
    const klass = ref<ClassKey>('L2')
    const slide = ref(0)
    const layer = ref(0)
    const bearer = ref<number | null>(0)
    const workStep = ref<number | null>(null)
    const engageStep = ref<number | null>(null)
    const faq = ref<string | null>('about-0')
    const copied = ref(false)
    const noticeHidden = ref(false)
    const speedState = ref<'idle' | 'running' | 'done' | 'error'>('idle')
    const speedVal = ref(0)

    try {
        noticeHidden.value = localStorage.getItem(NOTICE_LS_KEY) === NOTICE.id
    } catch { /* storage unavailable — keep the notice visible */ }

    const isCorp = computed(() => mode.value === 'corp')

    const activeTier = computed(() => TIERS[tier.value])
    const activeAdvance = computed(() => ADVANCE.find((a) => a.key === advance.value) ?? ADVANCE[0]!)
    const effectivePrice = computed(() => Math.round(activeTier.value.priceNum * (1 - activeAdvance.value.pct / 100)))
    const addonCount = computed(() => Object.values(homeAddons.value).filter(Boolean).length)

    const fmtNLe = (n: number) => 'NLe ' + n.toLocaleString('en-US')

    const setMode = (m: AudienceMode) => { mode.value = m }
    const toggleMode = () => { mode.value = isCorp.value ? 'retail' : 'corp' }
    const toggleAddon = (k: AddonKey) => { homeAddons.value[k] = !homeAddons.value[k] }
    const toggleBearer = (i: number) => { bearer.value = bearer.value === i ? null : i }
    const toggleWorkStep = (i: number) => { workStep.value = workStep.value === i ? null : i }
    const toggleEngageStep = (i: number) => { engageStep.value = engageStep.value === i ? null : i }
    const toggleFaq = (id: string) => { faq.value = faq.value === id ? null : id }

    const dismissNotice = () => {
        try { localStorage.setItem(NOTICE_LS_KEY, NOTICE.id) } catch { /* non-fatal */ }
        noticeHidden.value = true
    }

    let copyTimer: ReturnType<typeof setTimeout> | null = null
    const markCopied = () => {
        copied.value = true
        if (copyTimer) clearTimeout(copyTimer)
        copyTimer = setTimeout(() => { copied.value = false }, 1800)
    }

    // Real download test against this origin (see libs/speedtest.ts).
    const runSpeedTest = async () => {
        if (speedState.value === 'running') return
        speedState.value = 'running'
        speedVal.value = 0
        try {
            speedVal.value = await measureDownloadMbps({
                onProgress: (v) => { speedVal.value = v },
            })
            speedState.value = 'done'
        } catch {
            speedVal.value = 0
            speedState.value = 'error'
        }
    }

    let slideTimer: ReturnType<typeof setInterval> | null = null
    const startSlideshow = () => {
        if (slideTimer) return
        slideTimer = setInterval(() => { slide.value = (slide.value + 1) % PHOTOS.length }, 5000)
    }
    const stopSlideshow = () => {
        if (slideTimer) { clearInterval(slideTimer); slideTimer = null }
    }

    return {
        mode, tier, homeAddons, advance, klass, slide, layer, bearer, workStep, engageStep, faq,
        copied, noticeHidden, speedState, speedVal,
        isCorp, activeTier, activeAdvance, effectivePrice, addonCount,
        fmtNLe, setMode, toggleMode, toggleAddon, toggleBearer, toggleWorkStep, toggleEngageStep,
        toggleFaq, dismissNotice, markCopied, runSpeedTest, startSlideshow, stopSlideshow,
    }
})
